import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { Feature, FeatureDepartment } from '../../../types/feature';
import {
  ALL_BUS,
  BU,
  FeatureRoleMatrix,
  getFeatureTargetBUs,
} from '../utils/featureRoleMatrix';
import { getDeptMeta } from './DeptBadge';
import FeatureSectionRow from './FeatureSectionRow';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

interface FeatureBUTabsViewProps {
  features: Feature[];
  matrix: FeatureRoleMatrix;
  editable: boolean;
  onEdit: (f: Feature) => void;
  onDelete: (key: string) => void;
  onToggleStatus: (key: string) => void;
  onMakeCommon: (key: string, currentDept: FeatureDepartment) => void;
  onAddSubMenu: (parentKey: string, dept: FeatureDepartment) => void;
  onMoveSubMenu: (featureKey: string, newParentKey: string) => void;
}

// Sovereign Emerald per-BU underline rail colours (inline styles to avoid JIT purge of dynamic classes).
const TAB_UNDERLINE_COLOR: Record<BU, string> = {
  OMS: SOVEREIGN.brand,      // brand green
  AMS: '#6B5A8A',             // muted plum
  EPC: SOVEREIGN.champagne,   // champagne gold — NOT orange
};

function parseBuParam(raw: string | null): BU {
  const v = (raw ?? '').toUpperCase();
  return (ALL_BUS as string[]).includes(v) ? (v as BU) : 'OMS';
}

const FeatureBUTabsView: React.FC<FeatureBUTabsViewProps> = ({
  features,
  matrix,
  editable,
  onEdit,
  onDelete,
  onToggleStatus,
  onMakeCommon,
  onAddSubMenu,
  onMoveSubMenu,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeBU, setActiveBU] = useState<BU>(() => parseBuParam(searchParams.get('bu')));
  const [search, setSearch] = useState('');

  // Keep state in sync with URL changes (e.g. back/forward navigation).
  useEffect(() => {
    const next = parseBuParam(searchParams.get('bu'));
    if (next !== activeBU) setActiveBU(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSelectBU = (bu: BU) => {
    setActiveBU(bu);
    const next = new URLSearchParams(searchParams);
    next.set('bu', bu.toLowerCase());
    setSearchParams(next, { replace: true });
  };

  // Per-tab feature counts (ignore search; respect inactive filter applied upstream).
  const tabCounts = useMemo(() => {
    const counts: Record<BU, number> = { OMS: 0, AMS: 0, EPC: 0 };
    for (const f of features) {
      for (const bu of getFeatureTargetBUs(f)) {
        if (counts[bu] !== undefined) counts[bu] += 1;
      }
    }
    return counts;
  }, [features]);

  // Features visible in this tab (BU filter only).
  const tabFeatures = useMemo(
    () => features.filter(f => getFeatureTargetBUs(f).includes(activeBU)),
    [features, activeBU],
  );

  // Build sections: parents at root, children grouped, orphans tracked.
  const { sections, orphans } = useMemo(() => {
    const tabKeySet = new Set(tabFeatures.map(f => f.key));
    const parents = tabFeatures
      .filter(f => !f.parentKey)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const childrenByParent: Record<string, Feature[]> = {};
    const orphanList: Feature[] = [];
    for (const f of tabFeatures) {
      if (!f.parentKey) continue;
      if (tabKeySet.has(f.parentKey)) {
        (childrenByParent[f.parentKey] = childrenByParent[f.parentKey] ?? []).push(f);
      } else {
        orphanList.push(f);
      }
    }
    for (const k of Object.keys(childrenByParent)) {
      childrenByParent[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    orphanList.sort((a, b) => a.name.localeCompare(b.name));

    return {
      sections: parents.map(p => ({
        parent: p,
        children: childrenByParent[p.key] ?? [],
      })),
      orphans: orphanList,
    };
  }, [tabFeatures]);

  // Apply search filter — keep a parent section if itself or any child matches.
  const visibleSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sections;
    const matches = (f: Feature) =>
      f.name.toLowerCase().includes(q) ||
      f.key.toLowerCase().includes(q) ||
      f.route.toLowerCase().includes(q) ||
      f.permissionKey.toLowerCase().includes(q);
    return sections.filter(
      s => matches(s.parent) || s.children.some(matches),
    );
  }, [sections, search]);

  const visibleOrphans = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orphans;
    return orphans.filter(
      f =>
        f.name.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q) ||
        f.route.toLowerCase().includes(q) ||
        f.permissionKey.toLowerCase().includes(q),
    );
  }, [orphans, search]);

  const tabEmpty = tabFeatures.length === 0;
  const searchHidEverything =
    !tabEmpty && visibleSections.length === 0 && visibleOrphans.length === 0;

  return (
    <DndProvider backend={HTML5Backend}>
    <div className="space-y-3">
      {/* ── Tabs + search row ───────────────────────────────────────────── */}
      <div
        className="rounded-xl px-3 py-2.5 flex flex-wrap items-center gap-3"
        style={{
          background: SOVEREIGN.cardBg,
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          boxShadow: SOVEREIGN.cardShadow,
        }}
      >
        <div
          role="tablist"
          aria-label="Business unit"
          className="inline-flex rounded-lg p-1"
          style={{ background: 'rgba(11,59,46,0.05)' }}
        >
          {ALL_BUS.map(bu => {
            const meta = getDeptMeta(bu);
            const isActive = bu === activeBU;
            return (
              <button
                key={bu}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleSelectBU(bu)}
                className="relative inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors"
                style={
                  isActive
                    ? {
                        background: SOVEREIGN.cardBg,
                        color: SOVEREIGN.title,
                        boxShadow: '0 1px 4px rgba(11,59,46,0.12)',
                      }
                    : {
                        color: SOVEREIGN.kpiCaption,
                        background: 'transparent',
                      }
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: meta.style.dotColor }}
                  aria-hidden
                />
                <span>{bu}</span>
                <span
                  className="text-[10px] font-bold"
                  style={{ color: isActive ? meta.style.color : SOVEREIGN.kpiCaption }}
                >
                  · {tabCounts[bu]}
                </span>
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute left-3 right-3 -bottom-1 h-0.5 rounded-full"
                    style={{ background: TAB_UNDERLINE_COLOR[bu] }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="relative flex-1 min-w-[220px] max-w-md">
          <span
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            🔍
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search a menu, sub-menu, route, or permission key…"
            className="w-full pl-9 pr-9 py-2 text-sm rounded-lg transition-colors"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.ink,
              outline: 'none',
            }}
            onFocus={e => { (e.currentTarget as HTMLInputElement).style.borderColor = SOVEREIGN.title; }}
            onBlur={e => { (e.currentTarget as HTMLInputElement).style.borderColor = SOVEREIGN.inputBorder; }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              ✕
            </button>
          )}
        </div>

        <div className="text-xs ml-auto" style={{ color: SOVEREIGN.kpiCaption }}>
          {tabEmpty
            ? 'No features in this BU'
            : `Showing ${visibleSections.length} of ${sections.length} menu${
                sections.length === 1 ? '' : 's'
              }`}
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: SOVEREIGN.cardBg,
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          boxShadow: SOVEREIGN.cardShadow,
        }}
      >
        {tabEmpty ? (
          <EmptyTabState />
        ) : searchHidEverything ? (
          <SearchEmptyState
            activeBU={activeBU}
            query={search.trim()}
            onClear={() => setSearch('')}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead className="sticky top-0 z-[1]" style={{ background: 'rgba(11,59,46,0.04)', borderBottom: `1px solid ${SOVEREIGN.cardBorder}` }}>
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    Feature
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    Route
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    Permission
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-right"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    Roles
                  </th>
                  <th scope="col" className="px-2 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {visibleSections.map((section, idx) => (
                  <React.Fragment key={section.parent._id}>
                    {idx > 0 && (
                      <tr aria-hidden>
                        <td colSpan={5} className="h-2" style={{ background: SOVEREIGN.cardBg }} />
                      </tr>
                    )}
                    <FeatureSectionRow
                      parent={section.parent}
                      childrenList={section.children}
                      matrix={matrix}
                      editable={editable}
                      activeBU={activeBU}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onToggleStatus={onToggleStatus}
                      onMakeCommon={onMakeCommon}
                      onAddSubMenu={onAddSubMenu}
                      onMoveSubMenu={onMoveSubMenu}
                    />
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Orphan sub-menus — champagne at-risk tone ───────────────────── */}
      {visibleOrphans.length > 0 && (
        <div
          className="rounded-xl p-3"
          style={{
            background: 'rgba(201,169,97,0.06)',
            border: `1px solid rgba(201,169,97,0.30)`,
          }}
        >
          <div
            className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 flex items-center gap-1"
            style={{ color: '#8A5A08' }}
          >
            <span aria-hidden>⚠</span>
            Sub-features without a parent in {activeBU} ({visibleOrphans.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {visibleOrphans.map(o => (
              <div
                key={o._id}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{
                  background: SOVEREIGN.cardBg,
                  border: `1px solid rgba(201,169,97,0.24)`,
                }}
              >
                <span className="text-xs font-semibold truncate" style={{ color: SOVEREIGN.ink }}>
                  {o.name}
                </span>
                <span className="text-[10px] font-mono truncate" style={{ color: SOVEREIGN.kpiCaption }}>
                  {o.key}
                </span>
                <div className="flex-1" />
                {editable && (
                  <button
                    onClick={() => onEdit(o)}
                    className="text-xs font-semibold"
                    style={{ color: SOVEREIGN.bottle }}
                  >
                    Fix →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </DndProvider>
  );
};

function EmptyTabState() {
  return (
    <div className="py-16 text-center">
      <div className="text-5xl mb-3 opacity-30">📋</div>
      <div className="text-sm font-semibold" style={{ color: SOVEREIGN.ink }}>
        No features in this business unit yet
      </div>
      <div className="text-xs mt-1" style={{ color: SOVEREIGN.kpiCaption }}>
        Click "+ Add Feature" to create one.
      </div>
    </div>
  );
}

function SearchEmptyState({
  activeBU,
  query,
  onClear,
}: {
  activeBU: BU;
  query: string;
  onClear: () => void;
}) {
  return (
    <div className="py-12 text-center">
      <div className="text-4xl mb-2 opacity-30">🔭</div>
      <div className="text-sm font-semibold" style={{ color: SOVEREIGN.ink }}>
        No features in {activeBU} match &ldquo;{query}&rdquo;
      </div>
      <button
        onClick={onClear}
        className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-md text-white transition-colors"
        style={{ background: SOVEREIGN.btn }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = SOVEREIGN.btnHover; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = SOVEREIGN.btn; }}
      >
        Clear search
      </button>
    </div>
  );
}

export default FeatureBUTabsView;
